import type { NaturalizedDataset } from 'dcmjs';
import { SubstitutionLog } from './engine/charset';
import { toNaturalizedDataset } from './engine/compat';
import { DicomDataset } from './engine/dataset';
import { fromNaturalizedDataset } from './engine/denaturalize';
import { DicomFile } from './engine/reader';
import { Tag } from './engine/tag';
import * as tags from './engine/tags';
import { WritePolicy, writeDicomFile } from './engine/writer';

/** Distinguishes a parsed file from the keyword-addressed shape. */
function isDicomFile(value: unknown): value is DicomFile {
    return (
        value !== null &&
        typeof value === 'object' &&
        'dataset' in value &&
        'meta' in value &&
        'transferSyntax' in value &&
        (value as DicomFile).dataset instanceof DicomDataset
    );
}

/**
 * Realm-safe ArrayBuffer check.
 *
 * `instanceof` compares against the *current* realm's constructor, so a
 * buffer that crossed a realm boundary — a worker, or Node's `fs` under a
 * jsdom test environment — fails it while being a perfectly good
 * ArrayBuffer. `Object.prototype.toString` reads the internal slot instead
 * and is realm-independent.
 */
function isArrayBuffer(value: unknown): value is ArrayBuffer {
    return (
        value instanceof ArrayBuffer ||
        Object.prototype.toString.call(value) === '[object ArrayBuffer]'
    );
}

/**
 * Holds one DICOM object during editing, in whichever representation its
 * callers still need.
 *
 * A dataset the application parsed is held as the typed {@link DicomFile}.
 * Call sites that have not yet moved off the keyword-addressed shape reach it
 * through {@link dataset}, which projects the typed model on first access and
 * caches the result.
 *
 * Taking that projection has a consequence, which is why it is a method-like
 * getter rather than a field: call sites mutate the object they get back, so
 * from then on it — not the typed model — is what this wrapper serializes.
 * Serializing before anyone takes it writes each unedited attribute from the
 * bytes it was read from, and so reproduces the source exactly; serializing
 * after re-encodes every value from the projection. The engine's
 * byte-preservation guarantee therefore survives for paths that never touch
 * the projection, and those are the paths this migration is moving call sites
 * onto.
 */
export class DcmJsDatasetWrapper {
    private readonly file: DicomFile | undefined;
    private projection: unknown;
    private projectionTaken = false;

    constructor(dataset: unknown);
    constructor(file: DicomFile);
    constructor(source: unknown) {
        if (isDicomFile(source)) {
            this.file = source;
        } else {
            this.projection = source;
            this.projectionTaken = true;
        }
    }

    private static readonly _empty = new DcmJsDatasetWrapper(null);
    static empty(): DcmJsDatasetWrapper {
        return this._empty;
    }

    /**
     * The dataset in dcmjs's keyword-addressed shape.
     *
     * Projected from the typed model on first access. See the class comment
     * for why taking it changes what a later serialize produces.
     */
    public get dataset(): unknown {
        if (!this.projectionTaken) {
            this.projection = this.file
                ? toNaturalizedDataset(this.file)
                : undefined;
            this.projectionTaken = true;
        }
        return this.projection;
    }

    /**
     * The typed model, when it is still the authoritative copy.
     *
     * Undefined in two cases, and the second is the important one:
     *
     * - the wrapper was built from a plain object rather than a parsed file,
     *   so there is no typed form to read;
     * - something has taken the projection. Callers mutate what they get from
     *   it, so from that moment the typed model is a stale snapshot. Handing
     *   it out would let a read miss a write that had already happened, which
     *   is worse than not offering the fast path at all.
     */
    public get typed(): DicomDataset | undefined {
        return this.projectionTaken ? undefined : this.file?.dataset;
    }

    /**
     * Serialize the dataset to DICOM Part 10 bytes.
     *
     * The dataset is read, never written: serializing leaves the caller's
     * object — its `_meta` included — exactly as it was, so a caller may keep
     * using it afterwards. Every path that writes this dataset goes through
     * here so that rule lives in one place.
     *
     * Pass `log` to learn which values the dataset's character set could not
     * represent; see {@link SubstitutionLog}.
     */
    public toDicomBuffer(
        log?: SubstitutionLog,
        policy: WritePolicy = {}
    ): ArrayBuffer {
        // Nobody has taken the projection, so the typed model is still the
        // truth and each unedited attribute keeps its original bytes.
        if (this.file && !this.projectionTaken) {
            return writeDicomFile(this.file, log, policy);
        }
        const dataset = this.dataset;
        if (!dataset) {
            throw new Error('No dataset to serialize');
        }
        return writeDicomFile(
            fromNaturalizedDataset(dataset as NaturalizedDataset),
            log,
            policy
        );
    }

    /// Convert the dataset to a blob of DICOM data.
    public asBlob(log?: SubstitutionLog, policy: WritePolicy = {}): Blob {
        return new Blob([this.toDicomBuffer(log, policy)], {
            type: 'application/dicom',
        });
    }

    /**
     * The raw PixelData buffer of the dataset.
     *
     * Naturalized dcmjs datasets carry PixelData as a one-item array whose
     * entry holds the complete pixel value — all frames of a multi-frame
     * RTDOSE included — while encapsulated (compressed) data may carry one
     * entry per frame. In-place decompression
     * (decompressJpegLosslessInDataset) replaces the whole attribute with a
     * bare ArrayBuffer. Both shapes are accepted, and the first entry is
     * returned for the array shape.
     *
     * The shape is checked rather than cast: dcmjs also produces
     * `PixelData === 0` for a dataset read with `untilTag: '7FE00010'`, and
     * an unchecked value flows into `new Int16Array(...)`, which yields an
     * empty array instead of throwing — a blank slice or an empty dose grid
     * rendered with no error anywhere in the chain.
     */
    public rawPixelDataBuffer(): ArrayBuffer {
        const pixelData: unknown = (this.dataset as NaturalizedDataset)
            ?.PixelData;
        if (pixelData === null || pixelData === undefined) {
            throw new Error('Dataset has no PixelData');
        }
        const buffer: unknown = Array.isArray(pixelData)
            ? pixelData[0]
            : pixelData;
        if (!isArrayBuffer(buffer)) {
            throw new Error(
                'Dataset PixelData is not an ArrayBuffer ' +
                    `(got ${typeof buffer}); the dataset was not read with its pixel data.`
            );
        }
        return buffer;
    }

    public setCharacterSet(characterSet: string): void {
        (this.dataset as NaturalizedDataset).SpecificCharacterSet =
            characterSet;
    }

    /**
     * Rewrites the instance's own identity after the server minted a new one
     * (an edited registration is a NEW SOP instance, not a revision of the old
     * one). Both the dataset and the file meta carry the UID; leaving the meta
     * behind would export a file whose meta and dataset name different
     * instances, and a PACS reads the meta first.
     */
    public setStudyInstanceUid(studyInstanceUid: string): void {
        this.setUid('StudyInstanceUID', studyInstanceUid);
    }

    public setSeriesInstanceUid(seriesInstanceUid: string): void {
        this.setUid('SeriesInstanceUID', seriesInstanceUid);
    }

    private setUid(tag: string, value: string): void {
        if (!this.dataset || typeof this.dataset !== 'object') {
            return;
        }
        (this.dataset as Record<string, unknown>)[tag] = value;
    }

    public setSopInstanceUid(sopInstanceUid: string): void {
        if (!this.dataset || typeof this.dataset !== 'object') {
            return;
        }
        const dataset = this.dataset as Record<string, unknown>;
        dataset.SOPInstanceUID = sopInstanceUid;

        const meta = dataset._meta as Record<string, unknown> | undefined;
        if (!meta || !('MediaStorageSOPInstanceUID' in meta)) {
            return;
        }
        const existing = meta.MediaStorageSOPInstanceUID;
        // File meta reaches this class in two shapes: `{ vr, Value: [...] }`
        // for a dataset that was parsed, and a plain string for one assembled
        // in memory. Both are handled.
        if (
            existing !== null &&
            typeof existing === 'object' &&
            'Value' in existing
        ) {
            (existing as { Value: unknown[] }).Value = [sopInstanceUid];
        } else {
            meta.MediaStorageSOPInstanceUID = sopInstanceUid;
        }
    }

    public get PatientID(): string {
        return this.identityAttribute(tags.PATIENT_ID, 'PatientID') as string;
    }

    public get sopClassUID(): string {
        return this.identityAttribute(
            tags.SOP_CLASS_UID,
            'SOPClassUID'
        ) as string;
    }

    public get seriesInstanceUID(): string | undefined {
        return this.identityAttribute(
            tags.SERIES_INSTANCE_UID,
            'SeriesInstanceUID'
        );
    }

    /**
     * One of the short identifiers callers use to file an object, read from
     * whichever representation is authoritative.
     *
     * Answering these from the projection builds it, which copies every value
     * out of the file — a dose grid's 45 MB of pixel data included — to
     * produce a patient identifier the typed model reads from a view. Import
     * asks for one on every object it processes, so a series of a thousand
     * slices paid for a thousand projections and then held them, and a dose
     * that never needed the keyword-addressed shape at all ended up carrying
     * two copies of its pixels.
     *
     * Where the projection has already been taken it is also the only correct
     * source: a caller may have written to it, and the typed model is a stale
     * snapshot from that moment on.
     */
    private identityAttribute(tag: Tag, keyword: string): string | undefined {
        const typed = this.typed;
        if (typed) {
            return typed.string(tag);
        }
        const dataset = this.dataset;
        if (!dataset || typeof dataset !== 'object') {
            return undefined;
        }
        const value = (dataset as Record<string, unknown>)[keyword];
        return typeof value === 'string' ? value : undefined;
    }
}
